import store from '@/store'
import { getParameterByName } from '@/utils'
import { actionTo, actionType } from '@/utils/bridge'

/** g-click：App 外点击元素唤起 xinstall */
export const XINSTALL_CLICK_CLASS = 'g-click'
export const XINSTALL_TARGET_URL_ATTR = 'data-xinstall-target-url'

const WAKEUP_EVENT_TYPES = new Set(['app', 'RNLink'])

export function getXinstallClickClass(eventType) {
  return WAKEUP_EVENT_TYPES.has(eventType) ? XINSTALL_CLICK_CLASS : ''
}

export function getXinstallTargetUrl(eventType, link) {
  return WAKEUP_EVENT_TYPES.has(eventType) && link ? link : undefined
}

export function isNativeAppUserAgent() {
  return (
    /com.baidao.silver/gi.test(navigator.userAgent) ||
    /com.rjhy.uranus/gi.test(navigator.userAgent) ||
    localStorage.getItem('inApp') === '1'
  )
}

/** App 内原生桥跳转时合并当前页 query（仅 H5） */
export function appendCurrentSearchToUrl(targetUrl) {
  if (!targetUrl || !/^https?:\/\//i.test(targetUrl)) return targetUrl

  const currentSearch = window.location.search
  if (!currentSearch || currentSearch === '?') return targetUrl

  const incoming = new URLSearchParams(currentSearch.slice(1))
  const hashIndex = targetUrl.indexOf('#')

  if (hashIndex >= 0) {
    const beforeHash = targetUrl.slice(0, hashIndex)
    const hashPart = targetUrl.slice(hashIndex + 1)
    const hashQueryIndex = hashPart.indexOf('?')

    if (hashQueryIndex >= 0) {
      const hashPath = hashPart.slice(0, hashQueryIndex)
      const hashParams = new URLSearchParams(hashPart.slice(hashQueryIndex + 1))
      incoming.forEach((value, key) => {
        if (!hashParams.has(key)) hashParams.set(key, value)
      })
      return `${beforeHash}#${hashPath}?${hashParams.toString()}`
    }

    const [base, search = ''] = beforeHash.split('?')
    const beforeParams = new URLSearchParams(search)
    incoming.forEach((value, key) => {
      if (!beforeParams.has(key)) beforeParams.set(key, value)
    })
    const mergedSearch = beforeParams.toString()
    return mergedSearch
      ? `${base}?${mergedSearch}#${hashPart}`
      : `${beforeHash}#${hashPart}`
  }

  const queryIndex = targetUrl.indexOf('?')
  if (queryIndex >= 0) {
    const base = targetUrl.slice(0, queryIndex)
    const existing = new URLSearchParams(targetUrl.slice(queryIndex + 1))
    incoming.forEach((value, key) => {
      if (!existing.has(key)) existing.set(key, value)
    })
    return `${base}?${existing.toString()}`
  }

  return `${targetUrl}${currentSearch}`
}

export function handleJumpRealLinkInApp() {
  if (!isNativeAppUserAgent()) return
  const jumpRealLink = getParameterByName('jumpRealLink')
  if (!jumpRealLink) return
  actionTo(actionType.adver, { url: jumpRealLink })
}

const APP_KEY_MAP = {
  jfzgApp: 'muioeb2',
  hxgApp: '86qifjs',
  jfqlApp: '49xjscr',
  'com.rjhy.uranus': '49xjscr',
  fdApp: 'b153d0f',
  fdzqApp: 'b153d0f',
}
const DEFAULT_APP_KEY = '49xjscr'
const WECHAT_MASK =
  "<div style='background:rgba(0,0,0,0.7);position:fixed;left:0;top:0;filter:alpha(opacity=70);width:100%;height:100%;z-index:9999;'><div style='text-align:right; margin-top:1%; margin-right:5%;'><img style='width:70%; margin:0 auto;' src='https://cdn.xinstall.com/image/no_wxj.png'/></div></div>"

let xInstallInstance = null
let lastWakeupData = null
let wakeupLoading = false
let wakeupLoadingTimer = null
let bootstrapped = false

function callWakeup() {
  if (wakeupLoading || !xInstallInstance) return
  xInstallInstance.wakeupOrInstall()
  wakeupLoading = true
  clearTimeout(wakeupLoadingTimer)
  wakeupLoadingTimer = setTimeout(() => {
    wakeupLoading = false
  }, 500)
}

/** App 外唤起 xinstall；jumpLink 原样传 data.link，与 type 同级 */
function wakeXInstall(type, jumpLink, dataObj = {}) {
  const XInstall = window.XInstall
  if (!XInstall) return false

  const commonDsl = store.state.dslData?.commonDsl || {}
  const userInfo = store.state.userInfo || {}
  const routeQuery = Object.fromEntries(
    new URLSearchParams(window.location.search.slice(1)),
  )
  const appKey = dataObj.appKey || APP_KEY_MAP[commonDsl.appPlatform] || DEFAULT_APP_KEY
  if (!appKey) return false

  const payload = {
    ...(XInstall.parseUrlParams?.() || {}),
    source: routeQuery.source || commonDsl.source,
    platform_id: dataObj.platform_id || '',
    market_id: dataObj.market_id || '',
    union_id: routeQuery.xinstall_unionid || routeQuery.unionid || userInfo.unionid,
    username:
      routeQuery.xinstall_username ||
      userInfo.username ||
      userInfo.userData?.username,
  }
  if (type) {
    payload.type = type
    payload.jumpLink = jumpLink || location.href
  }

  if (
    lastWakeupData &&
    JSON.stringify(lastWakeupData) === JSON.stringify(payload) &&
    xInstallInstance
  ) {
    callWakeup()
    return true
  }

  lastWakeupData = payload
  window.xinstallObj = new XInstall(
    {
      appKey,
      isInstallModal: false,
      mask: () => WECHAT_MASK,
      onready() {
        xInstallInstance = this
        callWakeup()
      },
    },
    payload,
  )
  return true
}

export function bootstrapXInstall() {
  if (bootstrapped || typeof window === 'undefined') return
  if (isNativeAppUserAgent() || !window.XInstall) return
  bootstrapped = true

  window.wakeupOrInstall = (type, params = {}) => {
    if (!wakeXInstall(type || undefined, params.jumpLink, params.dataObj)) {
      console.warn('XInstall wakeupOrInstall failed')
    }
  }
}

export function bindXInstallClickDelegation() {
  const handler = (event) => {
    const el = event.target?.closest?.(`.${XINSTALL_CLICK_CLASS}`)
    if (!el) return
    const jumpLink = el.getAttribute(XINSTALL_TARGET_URL_ATTR)
    window.wakeupOrInstall?.('openWebView', { jumpLink: jumpLink || undefined })
  }
  document.addEventListener('click', handler, true)
  return () => document.removeEventListener('click', handler, true)
}

/** App 内走原生桥，App 外走 xinstall */
export function openAppWebView(jumpLink, dataObj = {}) {
  if (!jumpLink) return false

  if (isNativeAppUserAgent()) {
    const targetUrl = appendCurrentSearchToUrl(jumpLink)
    if (/^https?:\/\//i.test(targetUrl)) {
      actionTo(actionType.open_webviewQL, { webUrl: targetUrl })
    } else {
      actionTo(actionType.adver, { url: targetUrl })
    }
    return true
  }

  return wakeXInstall('openWebView', jumpLink, dataObj)
}
